import { Download, FilePlus2, FileText, FolderOpen, ImageIcon, Trash2, Upload } from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import type { RoomFile } from "@/api/generated/RoomFile"
import { describeError } from "@/api/mutation"
import {
  downloadRoomFile,
  useDeleteRoomFile,
  useRoomFile,
  useRoomFiles,
  useSaveRoomTextFile,
  useUploadRoomFile,
} from "@/api/queries/workplace"
import { useConfirm } from "@/components/app/confirm-dialog"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState, ErrorState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"
import { formatNumber } from "@/lib/format"

export function formatBytes(n: number) {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${formatNumber(n / 1024, { maximumFractionDigits: 0 })} KB`
  return `${formatNumber(n / 1024 / 1024, { maximumFractionDigits: 1 })} MB`
}

const KIND_ICON = { text: FileText, document: FileText, image: ImageIcon } as const

/** Editor state: `null` closed, `{}` new text file, `{ fileId }` view / edit. */
type Editor = null | { fileId?: number }

export function RoomFilesPanel({ roomId }: { roomId: number }) {
  const { t } = useTranslation()
  const files = useRoomFiles(roomId)
  const upload = useUploadRoomFile(roomId)
  const remove = useDeleteRoomFile(roomId)
  const confirm = useConfirm()
  const input = React.useRef<HTMLInputElement>(null)
  const [editor, setEditor] = React.useState<Editor>(null)

  const download = (f: RoomFile) =>
    downloadRoomFile(roomId, f).catch((e: unknown) => toast.error(describeError(e, t)))

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          ref={input}
          type="file"
          multiple
          className="hidden"
          tabIndex={-1}
          onChange={(e) => {
            for (const f of Array.from(e.target.files ?? [])) upload.mutate(f)
            e.target.value = ""
          }}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => input.current?.click()}
          disabled={upload.isPending}
        >
          {upload.isPending ? <Spinner /> : <Upload />}
          {t("workplace.room.files.upload")}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setEditor({})}>
          <FilePlus2 />
          {t("workplace.room.files.newText")}
        </Button>
      </div>
      {files.isPending ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : files.isError ? (
        <ErrorState variant="plain" error={files.error} onRetry={() => void files.refetch()} />
      ) : files.data.length === 0 ? (
        <EmptyState
          variant="plain"
          size="sm"
          icon={FolderOpen}
          title={t("workplace.room.files.emptyTitle")}
          description={t("workplace.room.files.emptyDescription")}
        />
      ) : (
        <ul className="space-y-1.5">
          {files.data.map((f) => {
            const Icon = KIND_ICON[f.kind as keyof typeof KIND_ICON] ?? FileText
            return (
              <li
                key={f.id}
                className="group/file flex items-center gap-2 rounded-lg border px-2.5 py-2"
                data-testid="room-file"
              >
                <Icon className="text-muted-foreground size-4 shrink-0" />
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left outline-none focus-visible:underline"
                  onClick={() => setEditor({ fileId: f.id })}
                >
                  <span className="block truncate text-[0.8125rem] font-medium">{f.original_filename}</span>
                  <span className="text-muted-foreground flex items-center gap-1 text-[0.6875rem]">
                    <span className="tabular-nums">{formatBytes(f.size_bytes)}</span>
                    <span aria-hidden>·</span>
                    <span className="truncate">
                      {f.created_by_worker_name ?? t("workplace.room.context.byPerson")}
                    </span>
                    <span aria-hidden>·</span>
                    <RelativeTime value={f.updated_at} />
                  </span>
                </button>
                {f.status === "pending" && (
                  <StatusBadge tone="info">{t("workplace.room.files.status.pending")}</StatusBadge>
                )}
                {f.status === "failed" && (
                  <StatusBadge tone="danger" title={f.extraction_error}>
                    {t("workplace.room.files.status.failed")}
                  </StatusBadge>
                )}
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className="text-muted-foreground"
                  aria-label={t("workplace.room.files.download", { name: f.original_filename })}
                  onClick={() => void download(f)}
                >
                  <Download />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className="text-muted-foreground hover:text-destructive"
                  aria-label={t("workplace.room.files.delete", { name: f.original_filename })}
                  onClick={() =>
                    void confirm({
                      title: t("workplace.room.files.deleteTitle"),
                      description: t("workplace.room.files.deleteDescription", { name: f.original_filename }),
                      confirmLabel: t("workplace.room.files.deleteConfirm"),
                      destructive: true,
                      onConfirm: () => remove.mutateAsync(f),
                    })
                  }
                >
                  <Trash2 />
                </Button>
              </li>
            )
          })}
        </ul>
      )}
      <FileEditor roomId={roomId} editor={editor} onClose={() => setEditor(null)} onDownload={download} />
    </div>
  )
}

function FileEditor({
  roomId,
  editor,
  onClose,
  onDownload,
}: {
  roomId: number
  editor: Editor
  onClose: () => void
  onDownload: (f: RoomFile) => void
}) {
  const { t } = useTranslation()
  const file = useRoomFile(roomId, editor?.fileId)
  const save = useSaveRoomTextFile(roomId)
  const [name, setName] = React.useState("")
  const [content, setContent] = React.useState("")
  const isNew = editor != null && editor.fileId == null
  const loaded = file.data
  const editable = isNew || loaded?.kind === "text"

  React.useEffect(() => {
    if (isNew) {
      setName("")
      setContent("")
    } else if (loaded) {
      setName(loaded.original_filename)
      setContent(loaded.extracted_text ?? "")
    }
  }, [isNew, loaded])

  const dirty = isNew
    ? !!name.trim()
    : !!loaded && (name !== loaded.original_filename || content !== (loaded.extracted_text ?? ""))

  return (
    <Dialog open={editor != null} onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent className="flex max-h-[90dvh] flex-col sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="truncate pr-6">
            {isNew ? t("workplace.room.files.newText") : (loaded?.original_filename ?? t("common.loading"))}
          </DialogTitle>
          <DialogDescription>
            {isNew
              ? t("workplace.room.files.newTextDescription")
              : editable
                ? t("workplace.room.files.editDescription")
                : t("workplace.room.files.readOnlyDescription")}
          </DialogDescription>
        </DialogHeader>
        {!isNew && file.isPending ? (
          <Skeleton className="h-64" />
        ) : !isNew && file.isError ? (
          <ErrorState variant="plain" error={file.error} onRetry={() => void file.refetch()} />
        ) : (
          <form
            id="room-file-form"
            className="flex min-h-0 flex-1 flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault()
              if (!editable || !name.trim()) return
              save.mutate({ id: editor?.fileId, filename: name.trim(), content }, { onSuccess: onClose })
            }}
          >
            {editable && (
              <div className="grid gap-1.5">
                <Label htmlFor="room-file-name">{t("workplace.room.files.filename")}</Label>
                <Input
                  id="room-file-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="notes.md"
                  className="font-mono text-[0.8125rem]"
                  autoFocus={isNew}
                />
              </div>
            )}
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              readOnly={!editable}
              aria-label={t("workplace.room.files.content")}
              className="min-h-64 flex-1 resize-y font-mono text-xs"
            />
            {loaded?.extraction_error && (
              <p className="text-destructive text-xs">{loaded.extraction_error}</p>
            )}
          </form>
        )}
        <DialogFooter>
          {loaded && (
            <Button type="button" variant="outline" className="sm:mr-auto" onClick={() => onDownload(loaded)}>
              <Download />
              {t("workplace.room.files.downloadShort")}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>
            {editable ? t("common.cancel") : t("workplace.close")}
          </Button>
          {editable && (
            <Button type="submit" form="room-file-form" disabled={!dirty || save.isPending}>
              {save.isPending && <Spinner />}
              {t("common.save")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
